export type RuntimeConfig = {
  schemaVersion: 2;
  convexUrl: string;
  release: string;
};

type RuntimeConfigFetcher = (
  input: RequestInfo | URL,
  init?: RequestInit
) => Promise<Pick<Response, "ok" | "status" | "json">>;

const RUNTIME_CONFIG_PATH = "/df-config.json";

let loadedConfig: RuntimeConfig | null = null;
let pendingConfig: Promise<RuntimeConfig> | null = null;

function isAllowedConvexUrl(value: string): boolean {
  try {
    const url = new URL(value);
    const isLocalHttp =
      url.protocol === "http:" && (url.hostname === "localhost" || url.hostname === "127.0.0.1");
    return (
      (url.protocol === "https:" || isLocalHttp) &&
      url.username === "" &&
      url.password === "" &&
      (url.pathname === "/" || url.pathname === "") &&
      url.search === "" &&
      url.hash === ""
    );
  } catch {
    return false;
  }
}

function isAllowedRelease(value: string): boolean {
  return value === "local" || /^[0-9a-f]{40}$/i.test(value);
}

export function parseRuntimeConfig(value: unknown): RuntimeConfig {
  if (!value || typeof value !== "object") {
    throw new Error("Runtime configuration must be a JSON object");
  }
  const candidate = value as Partial<RuntimeConfig>;
  if (candidate.schemaVersion !== 2) {
    throw new Error("Unsupported runtime configuration schema version");
  }
  if (typeof candidate.convexUrl !== "string" || !isAllowedConvexUrl(candidate.convexUrl)) {
    throw new Error("Runtime configuration contains an invalid Convex URL");
  }
  if (typeof candidate.release !== "string" || !isAllowedRelease(candidate.release)) {
    throw new Error("Runtime configuration contains an invalid web release");
  }
  return {
    schemaVersion: 2,
    convexUrl: candidate.convexUrl,
    release: candidate.release.toLowerCase(),
  };
}

export function loadRuntimeConfig(
  fetcher: RuntimeConfigFetcher = globalThis.fetch
): Promise<RuntimeConfig> {
  if (loadedConfig) return Promise.resolve(loadedConfig);
  if (pendingConfig) return pendingConfig;

  pendingConfig = fetchRuntimeConfig(fetcher)
    .then((config) => {
      loadedConfig = config;
      return config;
    })
    .catch((error) => {
      pendingConfig = null;
      throw error;
    });

  return pendingConfig;
}

function fetchRuntimeConfig(fetcher: RuntimeConfigFetcher): Promise<RuntimeConfig> {
  return fetcher(RUNTIME_CONFIG_PATH, {
    cache: "no-store",
    headers: { Accept: "application/json" },
  }).then(async (response) => {
    if (!response.ok) {
      throw new Error(`Runtime configuration request failed with HTTP ${response.status}`);
    }
    return parseRuntimeConfig(await response.json());
  });
}

export function fetchLatestRuntimeConfig(
  fetcher: RuntimeConfigFetcher = globalThis.fetch
): Promise<RuntimeConfig> {
  return fetchRuntimeConfig(fetcher);
}

export function getRuntimeConfig(): RuntimeConfig {
  if (!loadedConfig) {
    throw new Error("Runtime configuration has not been loaded");
  }
  return loadedConfig;
}

export function getRuntimeConvexUrl(): string | null {
  return loadedConfig?.convexUrl ?? null;
}

export function setRuntimeConfigForTests(config: RuntimeConfig | null): void {
  loadedConfig = config;
  pendingConfig = null;
}
